import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

/**********************************************
   Usage Notes:

   * clicking on the button opens the dropdown
   * if the menu is open, you can click anywhere outside of the menu and the dropdown will close
   * when you click on a menu item: the menu closes; that item's label is put into the
      button text; the clicked item will become the "selected item", and the onSelectionHandler
      method (passed in as a non-optional attribute by the container component) gets called
      with this item's model as its arg.
   * after selecting an item, if you reopen the menu, it will show the selected item
      highlighted and will scroll to it.

   In addition, once it has focus - either by tabbing (if it is given a tabIndex by its container)
     or by clicking on it - the RSDropdown responds to keyboard input, like:
       * arrow up or down keys will open the menu if it is closed
       * arrow up or down keys will move the highlighted menu item up or down if the menu is open
       * typing a string will jump to the first matching menu item; hitting backspace or
           waiting 600 ms will clear the typing buffer so you can start another search
       * hitting the Enter key when there is a highlighted menu item should act as if you had clicked on it

   If you wish to disengage the menu from the view hierarchy (especially useful if it is being
      clipped by one of its containing views), set the 'drawInBody' prop to true

   The default is that this menu drops DOWN but if you want it to drop UP, specify 'top' as the 'menuPlacement' prop

************************************************/

export var RSDropdown = function (_PureComponent) {
  _inherits(RSDropdown, _PureComponent);

  function RSDropdown(props) {
    _classCallCheck(this, RSDropdown);

    var _this = _possibleConstructorReturn(this, _PureComponent.call(this, props));

    _this.computeBounds = function () {
      var elemBounds = ReactDOM.findDOMNode(_this.dropdownRef.current).getBoundingClientRect();
      var top = void 0;
      if (_this.props.menuPlacement === 'top') {
        var listHeight = _this.props.menuStyle.itemHeight * _this.props.itemsList.length + _this.props.menuStyle.menuTopPad * 2 + _this.props.menuStyle.listBorderWidth * 2;
        listHeight = Math.min(listHeight, _this.props.menuStyle.listMaxHeight);
        if (_this.props.drawInBody) {
          top = elemBounds.top - listHeight;
        } else {
          top = -(listHeight + _this.props.menuStyle.buttonBorderWidth);
        }
      } else {
        if (_this.props.drawInBody) {
          top = elemBounds.bottom;
        } else {
          top = elemBounds.height - _this.props.menuStyle.buttonBorderWidth;
        }
      }
      _this.setState({
        bounds: elemBounds,
        dropTop: top
      });
    };

    _this.openMenu = function () {
      if (_this.props.menuDisabled) {
        return;
      }
      _this.computeBounds();
      _this.setState({
        showMenu: true,
        animatingMenu: _this.props.menuStyle.animateMenu,
        highlightedItemNdx: _this.state.selectedItemNdx
      });
      if (document.onclick) {
        document.onclick();
      }
      document.onclick = function () {
        _this.closeMenu(true);
      };
      setTimeout(function () {
        _this.scrollToHighlight(_this.state.highlightedItemNdx);
      }, 0);
    };

    _this.closeMenu = function (fast) {
      var animateClosing = fast ? false : _this.props.menuStyle.animateMenu;
      _this.setState({
        showMenu: false,
        animatingMenu: animateClosing
      });
      document.onclick = null;
    };

    _this.handleMenuAnimationEnd = function () {
      _this.setState({ animatingMenu: false });
    };

    _this.itemStr = '';

    _this.getNextItemStrStaleTime = function () {
      var itemStrStaleMilliseconds = 600;
      return Date.now() + itemStrStaleMilliseconds;
    };

    _this.itemStrStaleTime = _this.getNextItemStrStaleTime();

    _this.isItemStrStale = function () {
      return _this.itemStrStaleTime < Date.now();
    };

    _this.handleItemClick = function (itemNdx) {
      _this.closeMenu();
      var selectedItem = _this.props.itemsList[itemNdx];
      _this.setState({ selectedItemNdx: itemNdx, highlightedItemNdx: itemNdx });
      _this.props.onSelectionHandler(selectedItem);
    };

    _this.handleItemRollover = function (itemNdx) {
      _this.setState({ highlightedItemNdx: itemNdx });
    };

    _this.handleItemRollout = function (itemNdx) {
      if (_this.state.highlightedItemNdx === itemNdx) {
        _this.setState({
          highlightedItemNdx: -1
        });
      }
    };

    _this.handleMenuButtonClick = function () {
      if (_this.state.showMenu) {
        _this.closeMenu();
      } else {
        _this.openMenu();
      }
    };

    _this.handleMenuButtonRollover = function () {
      if (_this.props.onMenuButtonRollover) {
        _this.props.onMenuButtonRollover(_this.buttonRef);
      }
    };

    _this.handleMenuButtonRollout = function () {
      if (_this.props.onMenuButtonRollout) {
        _this.props.onMenuButtonRollout(_this.buttonRef);
      }
    };

    _this.handleKeyDown = function (event) {
      if (!!_this.props.menuDisabled) {
        return;
      }
      switch (event.key) {
        case 'Backspace':
          // backspace clears the scanning buffer
          _this.itemStr = '';
          event.preventDefault();
          break;
        case 'Escape':
          // escape closes the menu, but leaves it unfocused; this fixes that
          if (_this.state.showMenu) {
            _this.closeMenu();
            ReactDOM.findDOMNode(_this).focus();
          }
          break;
        case 'ArrowDown':
        case 'ArrowUp':
          if (_this.state.showMenu) {
            var newItemNdx = _this.state.highlightedItemNdx;
            if (event.key === 'ArrowDown') {
              // arrow down
              if (newItemNdx < _this.props.itemsList.length - 1) {
                newItemNdx++;
              }
            } else {
              // arrow up
              if (_this.state.highlightedItemNdx > 0) {
                newItemNdx--;
              }
            }
            if (newItemNdx !== _this.state.highlightedItemNdx) {
              event.preventDefault();
              _this.setState({ highlightedItemNdx: newItemNdx });
              _this.scrollToHighlight(newItemNdx);
            }
          } else {
            // menu was closed, up or down arrow opens it
            event.preventDefault();
            _this.openMenu();
          }
          break;
        default:
        // all other keys handled in handleKeyPress
      }
    };

    _this.handleKeyPress = function (event) {
      if (!!_this.props.menuDisabled) {
        return;
      }
      if (event.key === 'Enter') {
        if (_this.state.showMenu) {
          // if they have selected something and hit enter, choose that item
          if (_this.state.highlightedItemNdx >= 0) {
            event.preventDefault();
            _this.handleItemClick(_this.state.highlightedItemNdx);
            ReactDOM.findDOMNode(_this).focus();
          }
        } else {
          _this.openMenu();
        }
      } else {
        // all other text input feeds the scanning buffer
        if (event.isComposing || event.ctrlKey || event.metaKey || event.key === 'Tab') {
          // no non-text, tabs or control-keys please
          return;
        }
        if (_this.state.showMenu) {
          event.preventDefault();
          if (_this.isItemStrStale()) {
            _this.itemStr = '';
          }
          _this.itemStr += event.key;
          _this.itemStrStaleTime = _this.getNextItemStrStaleTime();
          var foundNdx = _this.findItemIndexForLabel(_this.itemStr);
          _this.setState({ highlightedItemNdx: foundNdx });
          _this.scrollToHighlight(foundNdx);
        }
      }
    };

    _this.findItemIndexForLabel = function (testLabel) {
      var lowerTestLabel = testLabel.toLowerCase();
      for (var i = 0; i < _this.props.itemsList.length; i++) {
        var anItem = _this.props.itemsList[i];
        var compareString = '' + anItem[_this.props.itemDisplayProperty];
        compareString = compareString.toLowerCase();
        if (compareString.indexOf(lowerTestLabel) === 0) {
          return i;
        }
      }
      return -1;
    };

    _this.scrollToHighlight = function (hiliteNdx) {
      if (hiliteNdx === -1) {
        return;
      }
      var itemElements = document.getElementsByTagName('li');
      if (itemElements.length === 0) {
        return;
      }
      var itemNdx = hiliteNdx;
      var itemElement = itemElements[itemNdx];
      var itemHeight = itemElement.clientHeight;
      var itemTop = itemHeight * itemNdx;
      var menuElems = document.getElementsByTagName('ul');
      if (menuElems.length === 0) {
        return;
      }
      var menuElem = menuElems[0];
      var itemBottom = itemTop + itemHeight;
      if (itemTop < menuElem.scrollTop || itemBottom > menuElem.scrollTop + menuElem.clientHeight) {
        var newScrollTop = itemTop - _this.props.menuStyle.menuTopPad;
        newScrollTop = Math.max(0, newScrollTop);
        var maxScroll = menuElem.scrollHeight - menuElem.clientHeight;
        newScrollTop = Math.min(maxScroll, newScrollTop);
        menuElem.scrollTop = newScrollTop;
      }
    };

    _this.state = {
      showMenu: false,
      animatingMenu: false,
      selectedItemNdx: -1,
      highlightedItemNdx: -1,
      dropTop: null,
      bounds: null
    };
    _this.buttonRef = React.createRef();
    _this.dropdownRef = React.createRef();
    return _this;
  }

  RSDropdown.getDerivedStateFromProps = function getDerivedStateFromProps(nextProps) {
    var selectedItemNdx = nextProps.itemsList.findIndex(function (anItem) {
      return anItem[nextProps.itemKeyProperty] === nextProps.selectedKey;
    });
    return {
      selectedItemNdx: selectedItemNdx
    };
  };

  // the user's keystrokes get saved in itemStr and this is used
  //  to match a menu item.  If they don't type for a little while
  //  itemStr gets cleared.  Backspace also clears it.


  /**
   * handleKeyDown handles arrows, backspace, and escape
   *  note that some browsers don't fire keyPress for arrow keys
   *  arrows up and down open the menu OR move the selection
   */


  /**
   * handleKeyPress is for enter, tab, and character keys
   */


  // ****** render
  RSDropdown.prototype.render = function render() {
    var _this2 = this;

    var menuWidget = void 0;
    if (this.state.showMenu || this.state.animatingMenu) {
      var animationToDo = '';
      if (this.state.animatingMenu) {
        animationToDo = this.state.showMenu ? 'showing' : 'hiding';
      }
      var menuItemsArray = this.props.itemsList.map(function (listItem, itemNdx) {
        return React.createElement(
          _this2.props.menuStyle.MenuItem,
          {
            'data-qa': 'MenuItem_' + itemNdx,
            key: listItem[_this2.props.itemKeyProperty],
            highlight: itemNdx === _this2.state.highlightedItemNdx,
            selected: itemNdx === _this2.state.selectedItemNdx,
            onClick: function onClick() {
              return _this2.handleItemClick(itemNdx);
            },
            onMouseEnter: function onMouseEnter() {
              return _this2.handleItemRollover(itemNdx);
            },
            onMouseLeave: function onMouseLeave() {
              return _this2.handleItemRollout(itemNdx);
            },
            isRtl: !!_this2.props.isRtl,
            disabled: listItem.disabled
          },
          listItem[_this2.props.itemDisplayProperty]
        );
      });
      menuWidget = React.createElement(
        this.props.menuStyle.MenuList,
        {
          'data-qa': 'MenuList',
          listTop: this.state.dropTop,
          listLeft: !this.props.drawInBody ? -this.props.menuStyle.buttonBorderWidth : this.state.bounds.left,
          menuWidth: this.state.bounds.width,
          onAnimationEnd: this.handleMenuAnimationEnd,
          animationToDo: animationToDo,
          isRtl: !!this.props.isRtl
        },
        menuItemsArray
      );
    }

    var buttonText = this.state.selectedItemNdx !== -1 && !this.props.buttonTextIsStatic ? this.props.itemsList[this.state.selectedItemNdx][this.props.itemDisplayProperty] : this.props.buttonText;

    return React.createElement(
      this.props.menuStyle.RSDropdownDiv,
      {
        'data-qa': this.props.dataQa || 'RSDropdownDiv',
        ref: this.dropdownRef,
        tabIndex: this.props.menuTabIndex,
        onKeyPress: this.handleKeyPress,
        onKeyDown: this.handleKeyDown,
        menuDisabled: !!this.props.menuDisabled
      },
      React.createElement(
        this.props.menuStyle.MenuButton,
        {
          'data-qa': 'MenuButton',
          ref: this.buttonRef,
          menuShowing: this.state.showMenu,
          onClick: this.handleMenuButtonClick,
          onMouseEnter: this.handleMenuButtonRollover,
          onMouseLeave: this.handleMenuButtonRollout,
          menuDisabled: !!this.props.menuDisabled
        },
        React.createElement(
          this.props.menuStyle.MenuButtonLabel,
          {
            'data-qa': 'MenuButtonLabel',
            menuShowing: this.state.showMenu,
            isRtl: !!this.props.isRtl,
            menuDisabled: !!this.props.menuDisabled
          },
          buttonText
        ),
        React.createElement(this.props.menuStyle.MenuButtonArrow, {
          'data-qa': 'DropdownArrow',
          menuShowing: this.state.showMenu,
          isRtl: !!this.props.isRtl,
          menuDisabled: !!this.props.menuDisabled
        })
      ),
      this.props.drawInBody ? ReactDOM.createPortal(menuWidget, document.body) : menuWidget
    );
  };

  return RSDropdown;
}(PureComponent);

RSDropdown.defaultProps = {
  drawInBody: false,
  menuPlacement: 'bottom',
  menuTabIndex: 0
};

RSDropdown.propTypes = {
  // menuTabIndex is the tab index of this widget
  menuTabIndex: PropTypes.number,

  // buttonText is what to display on the menu button when there is
  //  nothing yet selected
  buttonText: PropTypes.string,

  // buttonTextIsStatic is for cases where the button text does not
  //  change to be the value of the selected item
  buttonTextIsStatic: PropTypes.bool,

  // itemsList is an array of the objects to display
  itemsList: PropTypes.array.isRequired,

  // itemDisplayProperty is the property name of the field in the itemsList object
  //  which is the field you want to display in the menu
  itemDisplayProperty: PropTypes.string.isRequired,

  // itemKeyProperty is the property name of the field in the itemsList object
  //  which is the field to use as a key
  itemKeyProperty: PropTypes.string.isRequired,
  onSelectionHandler: PropTypes.func.isRequired,

  // selectedKey is the value of the key of the object currently selected in itemsList
  //  it is what is used to initialize the menu when it is mounted or re-mounted
  selectedKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  // if you want to replace with a custom data-qa, put it here
  dataQa: PropTypes.string,

  menuDisabled: PropTypes.bool,
  onMenuButtonRollover: PropTypes.func,
  onMenuButtonRollout: PropTypes.func,

  // is the interface right-to-left?
  isRtl: PropTypes.bool,

  // menuStyle must supply emotion factories for
  //  RSDropdownDiv
  //  MenuButton
  //  MenuButtonLabel
  //  MenuButtonArrow
  //  MenuList
  //  MenuItem
  //  menuStyle also exposes this information to RSDropdown, which it needs to know:
  //   menuTopPad - number - how much space above the top item is blank
  //   itemHeight - number - how high the items are
  //   animateMenu - boolean - whether the menu animates when it shows and hides
  //
  menuStyle: PropTypes.object.isRequired,

  // to allow the dropdown menu to escape the bounds of the parent
  // set drawInBody to true
  drawInBody: PropTypes.bool,

  // have the itemList open upward or down, "top" and "bottom", respectively
  menuPlacement: PropTypes.oneOf(['top', 'bottom'])
};


//////////////////
// WEBPACK FOOTER
// ./node_modules/@rosetta/rs-dropdown/es/components/RSDropdown.js
// module id = 676
// module chunks = 0